import { useMemo, useState } from 'react';
import { templateRowsFor } from '../../domain/checklistTemplates';
import { floorCountOf, OTHER_PRESET, ROOM_PRESETS, SPACE_PRESETS, SYSTEM_PRESETS, suggestedAreaInstance, type AreaPreset } from '../../domain/housePlan';
import type { V2Area } from '../../domain/inspection';
import { useT } from '../../i18n/i18n';
import { areaDisplayName, areaKindLabel, floorLabelFor, planNamingFor, roomLabel, spaceLabel, systemLabel } from '../../i18n/labels';
import { useStore } from '../../state/createStore';
import { addAreaAction, areasOf, inspectionStore } from '../../state/inspectionStore';
import { Button, Chip, Field, SectionEyebrow, SheetShell, TextInput, showToast } from '../../ui/components';

// "+ Add Area" (Authority §6.1 / §6.4): pick a preset (room / space / system / custom), the floor
// for floor-bound presets, an optional name — and one more Area record with its own checklist rows
// exists (KEEP addAreaAction; presets map onto existing kinds, checklist content untouched).
// Instance names are suggested in the App Language in force and continue the existing series.
type PresetOption = { key: string; label: string; base: string; kind: AreaPreset['kind']; floorBound: boolean };

export function AddAreaSheet(props: {
  inspectionId: string;
  // Initial floor: a floor number, null (not on a floor) or undefined (first floor of the house).
  initialFloor?: number | null;
  initialPresetKey?: string;
  onClose: () => void;
  onAdded?: (area: V2Area) => void;
}) {
  const { t, tp, locale } = useT();
  const state = useStore(inspectionStore, (s) => s);
  const areas = useMemo(() => areasOf(state, props.inspectionId), [state, props.inspectionId]);
  const naming = useMemo(() => planNamingFor(locale), [locale]);
  // The series continues from the names the list SHOWS: an area named under another App Language, or
  // under the British floor names before 2026-09-29, keeps its stored text while its label re-expresses
  // it — the stored text would restart or misnumber the series.
  const shownNames = useMemo(() => areas.map((a) => ({ name: areaDisplayName(locale, a), floor: a.floor })), [areas, locale]);
  const floorCount = floorCountOf(areas);

  const groups = useMemo<Array<{ key: string; label: string; presets: PresetOption[] }>>(
    () => [
      {
        key: 'rooms',
        label: t('layout.presetRooms'),
        presets: ROOM_PRESETS.map((p) => ({ key: p.key, label: roomLabel(t, p.key), base: naming.roomBase[p.key], kind: p.kind, floorBound: true }))
      },
      {
        key: 'spaces',
        label: t('layout.presetSpaces'),
        presets: SPACE_PRESETS.map((p) => ({ key: p.key, label: spaceLabel(t, p.key), base: naming.spaceBase[p.key], kind: p.kind, floorBound: false }))
      },
      {
        key: 'systems',
        label: t('layout.presetSystems'),
        presets: SYSTEM_PRESETS.map((p) => ({ key: p.key, label: systemLabel(t, p.key), base: naming.systemName[p.key], kind: p.kind, floorBound: false }))
      },
      {
        key: 'custom',
        label: t('layout.presetOther'),
        presets: [{ key: OTHER_PRESET.key, label: areaKindLabel(t, 'other'), base: areaKindLabel(t, 'other'), kind: OTHER_PRESET.kind, floorBound: true }]
      }
    ],
    [t, naming]
  );
  const allPresets = useMemo(() => groups.flatMap((g) => g.presets), [groups]);

  const [presetKey, setPresetKey] = useState(props.initialPresetKey ?? 'bedroom');
  const [floor, setFloor] = useState<number | null>(props.initialFloor === undefined ? (floorCount > 0 ? 1 : null) : props.initialFloor);
  const [customName, setCustomName] = useState('');
  const [busy, setBusy] = useState(false);

  const preset = allPresets.find((p) => p.key === presetKey) ?? allPresets[0];
  const effectiveFloor = preset.floorBound ? floor : null;
  const suggested = suggestedAreaInstance(preset.key, preset.base, effectiveFloor, shownNames, naming.floorPrefix);
  const previewName = suggested.name;
  const finalName = customName.trim().length > 0 ? customName.trim() : previewName;
  // A preset left unnamed is a plan-style instance: its label lets the Report Language re-express the
  // name (task book §3 / §18). A name the inspector typed, or a custom area, is the inspector's own
  // text and prints as written.
  const label = customName.trim().length > 0 || preset.key === OTHER_PRESET.key ? null : suggested.label;
  const knownFloors = Math.max(floorCount, effectiveFloor ?? 0, 1);
  const floorOptions: Array<{ value: number | null; label: string; testId: string }> = [
    ...Array.from({ length: knownFloors }, (_, i) => ({ value: i + 1, label: floorLabelFor(t, i + 1), testId: `add-floor-${i + 1}` })),
    { value: knownFloors + 1, label: t('layout.newFloor', { floor: floorLabelFor(t, knownFloors + 1) }), testId: `add-floor-${knownFloors + 1}` },
    { value: null, label: t('layout.noFloor'), testId: 'add-floor-none' }
  ];

  async function handleAdd() {
    setBusy(true);
    try {
      const area = await addAreaAction(props.inspectionId, { kind: preset.kind, name: finalName, floor: effectiveFloor, label });
      showToast(tp('layout.added', templateRowsFor(preset.kind).length, { name: area.name }));
      props.onAdded?.(area);
      props.onClose();
    } finally {
      setBusy(false);
    }
  }

  return (
    <SheetShell
      title={t('layout.addAreaTitle')}
      onClose={props.onClose}
      testId="add-area-sheet"
      footer={
        <>
          <Button onClick={() => void handleAdd()} disabled={busy} testId="add-area">
            {t('layout.addNamed', { name: finalName })}
          </Button>
          <Button variant="text" onClick={props.onClose}>
            {t('shell.cancel')}
          </Button>
        </>
      }
    >
      {groups.map((group) => (
        <div className="preset-group" key={group.key}>
          <SectionEyebrow>{group.label}</SectionEyebrow>
          <div className="chips">
            {group.presets.map((p) => (
              <Chip key={p.key} label={p.label} selected={p.key === preset.key} onClick={() => setPresetKey(p.key)} testId={`add-preset-${p.key}`} />
            ))}
          </div>
        </div>
      ))}
      {preset.floorBound ? (
        <div className="preset-group" data-testid="add-area-floor">
          <SectionEyebrow>{t('layout.floor')}</SectionEyebrow>
          <div className="chips">
            {floorOptions.map((opt) => (
              <Chip key={opt.testId} label={opt.label} selected={opt.value === floor} onClick={() => setFloor(opt.value)} testId={opt.testId} />
            ))}
          </div>
        </div>
      ) : null}
      <Field label={t('layout.areaName')} htmlFor="add-area-name">
        <TextInput id="add-area-name" value={customName} onChange={setCustomName} placeholder={t('layout.areaNamePlaceholder', { name: previewName })} testId="add-area-name" />
      </Field>
      <p className="card-note">{tp('common.itemsCount', templateRowsFor(preset.kind).length)}</p>
    </SheetShell>
  );
}
